import React, { useEffect, useRef, useState } from "react";
import {
  PlayCircle,
  TrainFront,
  FileStack,
  Sparkles,
  MessageSquareText,
  ShieldCheck,
  Cctv,
} from "lucide-react";
import SectionHeading from "./SectionHeading";

import Railway from "../Images/Railway.png";
import ottVideo from "../Videos/OTT.mp4";
import docImg from "../Images/doclift.png";
import vigilImg from "../Images/vigil.png";
import saasImg from "../Images/Aaas.png";
import railplotImg from "../Images/railplot.png";

const products = [
  {
    id: 1,
    title: "Vxiomatic",
    tag: "OTT Platform",
    icon: PlayCircle,
    description:
      "Vxiomatic CMS is a cloud-based OTT content platform that enables video management, monetization, multi-platform streaming, and secure content delivery with advanced customization and analytics.",
    video: ottVideo,
  },
  {
    id: 2,
    title: "Railplot",
    tag: "Railway Analytics",
    icon: TrainFront,
    description:
      "Our railway analytics product. It turns raw locomotive speedometer data from Laxven, Medha, Raitech, and Telpro into live dashboards — speed violations, pre-braking analysis, and crew management in one place.",
    image: railplotImg,
  },
  {
    id: 3,
    title: "Doclift",
    tag: "Business Automation",
    icon: MessageSquareText,
    description:
      "DocLift enables businesses to automate WhatsApp interactions, streamline appointment and service request management, and gain insights into physical space operations.",
    image: docImg,
  },
  {
    id: 4,
    title: "Aaas",
    tag: "Authorization Platform",
    icon: ShieldCheck,
    description:
      "Authorization-as-a-Service (AaaS) provides secure, policy-based access control for any datasource through configurable APIs. It enables data sharing with fine-grained authorization, masking, and threat protection.",
    image: saasImg,
  },
  {
    id: 5,
    title: "Vigil",
    tag: "AI Surveillance",
    icon: Cctv,
    description:
      "Vigil is an AI-powered vigilance system that detects and alerts on identified individuals, enabling real-time tracking across multiple cameras to monitor movement and enhance security.",
    image: vigilImg,
  },
];

export default function OurProducts() {
  const scrollRef = useRef(null);
  const [activeDot, setActiveDot] = useState(0);

  const pages = [
    [0, 1], // First 2 products
    [2, 3], // Last 2 products
  ];

  const handleDotClick = (pageIndex) => {
    setActiveDot(pageIndex);

    const firstProductIndex = pages[pageIndex][0];

    document
      .getElementById(`product-${firstProductIndex}`)
      ?.scrollIntoView({
        behavior: "smooth",
        inline: "start",
        block: "nearest",
      });
  };

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleScroll = () => {
      const halfway =
        (container.scrollWidth - container.clientWidth) / 2;

      if (container.scrollLeft >= halfway) {
        setActiveDot(1);
      } else {
        setActiveDot(0);
      }
    };

    container.addEventListener("scroll", handleScroll);

    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <section className="relative overflow-hidden bg-[#F7F8FA] py-24 px-6 sm:px-12 text-slate-900">
      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <SectionHeading
            eyebrow="Built In-House"
            title="Products We Build"
            description="Beyond client work, we build and maintain our own products — here's what we're shipping ourselves."
            theme="light"
            titleClassName="mb-0"
            descriptionClassName="max-w-4xl mx-auto"
          />
        </div>

        {/* Products */}
        <div
          ref={scrollRef}
          className="products-scroll overflow-x-auto flex gap-6 snap-x snap-mandatory pb-6"
        >
          {products.map((item, index) => {
            const Icon = item.icon;

            return (
              <div
                key={item.id}
                id={`product-${index}`}
                className="snap-center flex-shrink-0 w-[320px] md:w-[400px] rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"
              >
                {/* Image / Video */}
                <div className="relative aspect-[16/10] bg-slate-100">
                  {item.video ? (
                    <video
                      src={item.video}
                      className="w-full h-full object-cover"
                      autoPlay
                      muted
                      loop
                      playsInline
                    />
                  ) : item.image ? (
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center w-full h-full bg-slate-100">
                      <Icon className="w-16 h-16 text-slate-300" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-[#60a5fa] mb-3">
                    <Icon className="w-4 h-4" />
                    {item.tag}
                  </div>

                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {item.title}
                  </h3>

                  <p className="text-slate-600 leading-relaxed text-sm">
                    {item.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Dots */}
        <div className="flex justify-center mt-8 gap-3">
          {pages.map((_, index) => (
            <button
              key={index}
              onClick={() => handleDotClick(index)}
              className={`h-3 rounded-full transition-all duration-300 ${activeDot === index
                ? "w-8 bg-[#355dff]"
                : "w-3 bg-slate-300 hover:bg-slate-400"
                }`}
            />
          ))}
        </div>
      </div>

      {/* Hide Scrollbar */}
      <style>{`
        .products-scroll{
          scrollbar-width:none;
          -ms-overflow-style:none;
        }

        .products-scroll::-webkit-scrollbar{
          display:none;
        }
      `}</style>
    </section>
  );
}